import React from 'react';
import { createPortal } from 'react-dom';
import { Pencil } from 'lucide-react';
import type { KanbanLabel } from 'types/kanban';
import styles from './CardDetailLabelsPanel.module.scss';

export interface CardDetailLabelsPanelProps {
  show: boolean;
  anchor: DOMRect | null;
  labelSearch: string;
  onLabelSearchChange: (v: string) => void;
  filteredLabels: KanbanLabel[];
  cardLabelIds: string[];
  onToggleLabel: (labelId: string) => void;
  onEditLabelClick: (label: KanbanLabel) => void;
  onCreateLabelClick: () => void;
  onClose: () => void;
}

export default function CardDetailLabelsPanel({
  show,
  anchor,
  labelSearch,
  onLabelSearchChange,
  filteredLabels,
  cardLabelIds,
  onToggleLabel,
  onEditLabelClick,
  onCreateLabelClick,
  onClose,
}: CardDetailLabelsPanelProps) {
  if (!show || !anchor) return null;

  return createPortal(
    <>
      <div className={styles.panelBackdrop} onClick={onClose} aria-hidden />
      <div
        className={styles.labelsPanel}
        style={{
          position: 'fixed',
          top: anchor.bottom + 6,
          left: anchor.left,
          minWidth: 280,
        }}
        role="dialog"
        aria-label="Labels"
      >
        <input
          type="text"
          className={styles.panelSearchInput}
          placeholder="Search labels"
          value={labelSearch}
          onChange={(e) => onLabelSearchChange(e.target.value)}
          aria-label="Search labels"
        />
        <ul className={styles.labelsPanelList}>
          {filteredLabels.map((label) => (
            <li
              key={label.id}
              className={styles.labelsPanelItem}
              role="button"
              tabIndex={0}
              aria-label={`Toggle label ${label.name}`}
              aria-pressed={cardLabelIds.includes(label.id)}
              onClick={(e) => {
                // Keep checkbox behavior as-is; prevent double toggles.
                if ((e.target as HTMLElement).tagName === 'INPUT') return;
                onToggleLabel(label.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleLabel(label.id);
                }
              }}
            >
              <input
                type="checkbox"
                checked={cardLabelIds.includes(label.id)}
                onChange={() => onToggleLabel(label.id)}
                onClick={(e) => {
                  // Avoid li onClick firing after checkbox click.
                  e.stopPropagation();
                }}
                aria-label={`Label ${label.name}`}
              />
              <span
                className={styles.labelColorChip}
                style={{ background: label.color }}
              />
              <span className={styles.labelName}>{label.name}</span>
              <button
                type="button"
                className={styles.labelEditBtn}
                aria-label={`Edit label ${label.name}`}
                onClick={(e) => {
                  // Don't toggle selection when clicking the edit icon.
                  e.stopPropagation();
                  onEditLabelClick(label);
                }}
              >
                <Pencil size={14} />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className={styles.createLabelBtn}
          onClick={onCreateLabelClick}
        >
          Create a new label
        </button>
      </div>
    </>,
    document.body,
  );
}
